import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../services/api'
import TeamHelmet from '../components/TeamHelmet'
import UserAvatar from '../components/UserAvatar'
import './GameDetails.css'

interface PlayerStatLine {
  slot: string
  position: string
  name?: string
  passing?: {
    attempts: number
    completions: number
    yards: number
    touchdowns: number
    interceptions: number
  }
  rushing?: {
    attempts: number
    yards: number
    touchdowns: number
  }
  receiving?: {
    receptions: number
    yards: number
    touchdowns: number
  }
  returns?: {
    kickReturns: number
    kickReturnYards: number
    kickReturnTouchdowns: number
    puntReturns: number
    puntReturnYards: number
    puntReturnTouchdowns: number
  }
  defense?: {
    tackles: number
    sacks: number
    interceptions: number
    interceptionYards: number
    interceptionTouchdowns: number
  }
  kicking?: {
    extraPointAttempts: number
    extraPointsMade: number
    fieldGoalAttempts: number
    fieldGoalsMade: number
  }
  punting?: {
    punts: number
    yards: number
    averageYards: number
  }
}

interface TeamDetails {
  abbreviation: string
  score: number
  quarterScores: number[]
  firstDowns: number
  fumblesTotal: number
  fumblesLost: number
  timeOfPossessionSeconds: number
  fourthDownStops: number
  fieldGoalBlocks?: number
  injuries?: number
  players: PlayerStatLine[]
}

interface GameDetails {
  id: number
  homeTeam: TeamDetails
  awayTeam: TeamDetails
  playedAt: string | null
  uploadedAt: string
  player1User?: {
    id: number
    username: string
    displayName: string
    discordId: string | null
    discordAvatar: string | null
    teamAbbreviation: string
  }
  player2User?: {
    id: number
    username: string
    displayName: string
    discordId: string | null
    discordAvatar: string | null
    teamAbbreviation: string
  }
}

export default function GameDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [game, setGame] = useState<GameDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchGameDetails = async () => {
      try {
        setLoading(true)
        const response = await api.get<GameDetails>(`/games/${id}`)
        setGame(response.data)
        setError(null)
      } catch (err: any) {
        console.error('Error fetching game details:', err)
        setError(err.response?.data?.error || 'Failed to load game details.')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchGameDetails()
    }
  }, [id])

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown'
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return 'Unknown'
    }
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const renderPassing = (passing?: PlayerStatLine['passing']) => {
    if (!passing || passing.attempts === 0) return '—'
    return `${passing.completions}/${passing.attempts}, ${passing.yards} yds, ${passing.touchdowns} TD, ${passing.interceptions} INT`
  }

  const renderRushing = (rushing?: PlayerStatLine['rushing']) => {
    if (!rushing || rushing.attempts === 0) return '—'
    return `${rushing.attempts} att, ${rushing.yards} yds, ${rushing.touchdowns} TD`
  }

  const renderReceiving = (receiving?: PlayerStatLine['receiving']) => {
    if (!receiving || receiving.receptions === 0) return '—'
    return `${receiving.receptions} rec, ${receiving.yards} yds, ${receiving.touchdowns} TD`
  }

  const renderReturns = (returns?: PlayerStatLine['returns']) => {
    if (!returns) return '—'
    const parts: string[] = []
    if (returns.kickReturns > 0) {
      parts.push(`${returns.kickReturns} KR, ${returns.kickReturnYards} yds`)
      if (returns.kickReturnTouchdowns > 0) parts.push(`${returns.kickReturnTouchdowns} TD`)
    }
    if (returns.puntReturns > 0) {
      parts.push(`${returns.puntReturns} PR, ${returns.puntReturnYards} yds`)
      if (returns.puntReturnTouchdowns > 0) parts.push(`${returns.puntReturnTouchdowns} TD`)
    }
    return parts.length > 0 ? parts.join(', ') : '—'
  }

  const renderDefense = (defense?: PlayerStatLine['defense']) => {
    if (!defense || defense.tackles === 0) return '—'
    const parts = [`${defense.tackles} Tk`]
    if (defense.sacks > 0) parts.push(`${defense.sacks} Sk`)
    if (defense.interceptions > 0) {
      parts.push(`${defense.interceptions} INT`)
      if (defense.interceptionYards !== 0) {
        parts.push(`${defense.interceptionYards} yds`)
      }
      if (defense.interceptionTouchdowns > 0) {
        parts.push(`${defense.interceptionTouchdowns} TD`)
      }
    }
    return parts.join(', ')
  }

  const renderKicking = (kicking?: PlayerStatLine['kicking']) => {
    if (!kicking) return '—'
    return `XP ${kicking.extraPointsMade}/${kicking.extraPointAttempts}, FG ${kicking.fieldGoalsMade}/${kicking.fieldGoalAttempts}`
  }

  const renderPunting = (punting?: PlayerStatLine['punting']) => {
    if (!punting) return '—'
    const parts = [`${punting.punts} punts`, `${punting.yards} yds`]
    if (punting.punts > 0) {
      parts.push(`${punting.averageYards.toFixed(1)} avg`)
    }
    return parts.join(', ')
  }

  const renderTeamPlayers = (team: TeamDetails) => {
    if (!team.players || team.players.length === 0) {
      return null
    }

    const playersWithStats = team.players.filter(player => {
      const hasOffense =
        (player.passing && (player.passing.attempts > 0 || player.passing.yards > 0 || player.passing.touchdowns > 0 || player.passing.interceptions > 0)) ||
        (player.rushing && (player.rushing.attempts > 0 || player.rushing.yards > 0 || player.rushing.touchdowns > 0)) ||
        (player.receiving && (player.receiving.receptions > 0 || player.receiving.yards > 0 || player.receiving.touchdowns > 0)) ||
        (player.returns &&
          (player.returns.kickReturns > 0 || player.returns.puntReturns > 0 || player.returns.kickReturnYards > 0 || player.returns.puntReturnYards > 0 || player.returns.kickReturnTouchdowns > 0 || player.returns.puntReturnTouchdowns > 0))
      const hasDefense =
        player.defense &&
        (player.defense.tackles > 0 ||
          player.defense.sacks > 0 ||
          player.defense.interceptions > 0 ||
          player.defense.interceptionYards > 0 ||
          player.defense.interceptionTouchdowns > 0)
      const hasSpecial =
        (player.kicking &&
          (player.kicking.extraPointAttempts > 0 || player.kicking.fieldGoalAttempts > 0 || player.kicking.extraPointsMade > 0 || player.kicking.fieldGoalsMade > 0)) ||
        (player.punting && (player.punting.punts > 0 || player.punting.yards > 0))

      return hasOffense || hasDefense || hasSpecial
    })

    if (playersWithStats.length === 0) {
      return null
    }

    return (
      <div className="player-stats">
        <h4>{team.abbreviation} Player Statistics</h4>
        <div className="player-table-wrapper">
          <table className="player-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Slot</th>
                <th>Pos</th>
                <th>Passing</th>
                <th>Rushing</th>
                <th>Receiving</th>
                <th>Returns</th>
                <th>Defense</th>
                <th>Kicking</th>
                <th>Punting</th>
              </tr>
            </thead>
            <tbody>
              {playersWithStats.map(player => (
                <tr key={`${team.abbreviation}-${player.slot}`}>
                  <td>{player.name ?? player.slot}</td>
                  <td>{player.slot}</td>
                  <td>{player.position}</td>
                  <td>{renderPassing(player.passing)}</td>
                  <td>{renderRushing(player.rushing)}</td>
                  <td>{renderReceiving(player.receiving)}</td>
                  <td>{renderReturns(player.returns)}</td>
                  <td>{renderDefense(player.defense)}</td>
                  <td>{renderKicking(player.kicking)}</td>
                  <td>{renderPunting(player.punting)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const getInterceptionsThrown = (team: TeamDetails): number =>
    team.players.reduce((total, player) => total + (player.passing?.interceptions ?? 0), 0)

  const getTotalTackles = (team: TeamDetails): number =>
    team.players.reduce((total, player) => total + (player.defense?.tackles ?? 0), 0)

  const getPassingYards = (team: TeamDetails): number =>
    team.players.reduce((total, player) => total + (player.passing?.yards ?? 0), 0)

  const getRushingYards = (team: TeamDetails): number =>
    team.players.reduce((total, player) => total + (player.rushing?.yards ?? 0), 0)

  const getOffensiveYards = (team: TeamDetails): number =>
    getPassingYards(team) + getRushingYards(team)

  const getSpecialTeamYards = (team: TeamDetails): number =>
    team.players.reduce(
      (total, player) =>
        total +
        (player.returns?.kickReturnYards ?? 0) +
        (player.returns?.puntReturnYards ?? 0),
      0
    )

  const getTotalSacks = (team: TeamDetails): number =>
    team.players.reduce((total, player) => total + (player.defense?.sacks ?? 0), 0)

  const renderTeamStatsGrid = (team: TeamDetails) => (
    <div className="team-stats-grid">
      <div>
        <span className="label">Offensive yards</span>
        <span className="value">{getOffensiveYards(team)}</span>
      </div>
      <div>
        <span className="label">Passing</span>
        <span className="value">{getPassingYards(team)}</span>
      </div>
      <div>
        <span className="label">Rushing</span>
        <span className="value">{getRushingYards(team)}</span>
      </div>
      <div>
        <span className="label">Special teams yards</span>
        <span className="value">{getSpecialTeamYards(team)}</span>
      </div>
      <div>
        <span className="label">1st Downs</span>
        <span className="value">{team.firstDowns}</span>
      </div>
      <div>
        <span className="label">TOP</span>
        <span className="value">{formatTime(team.timeOfPossessionSeconds)}</span>
      </div>
      <div>
        <span className="label">Tackles</span>
        <span className="value">{getTotalTackles(team)}</span>
      </div>
      <div>
        <span className="label">Sacks</span>
        <span className="value">{getTotalSacks(team)}</span>
      </div>
      <div>
        <span className="label">4th Down Stops</span>
        <span className="value">{team.fourthDownStops}</span>
      </div>
      <div>
        <span className="label">INT Thrown</span>
        <span className="value">{getInterceptionsThrown(team)}</span>
      </div>
      <div>
        <span className="label">LOST FUM/TOTAL</span>
        <span className="value">
          {team.fumblesLost}/{team.fumblesTotal}
        </span>
      </div>
      <div>
        <span className="label">Injuries</span>
        <span className="value">{team.injuries ?? 0}</span>
      </div>
    </div>
  )

  if (loading) {
    return <div className="game-details loading">Loading game details...</div>
  }

  if (error) {
    return (
      <div className="game-details">
        <div className="error-message">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Back to Command Center
        </button>
      </div>
    )
  }

  if (!game) {
    return (
      <div className="game-details">
        <div className="error-message">Game not found</div>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Back to Command Center
        </button>
      </div>
    )
  }

  return (
    <div className="game-details">
      <div className="game-header">
        <button className="btn btn-secondary back-btn" onClick={() => navigate('/')}>
          Back
        </button>
        <h1>Game Details</h1>
      </div>

      <div className="scoreboard">
        <div className="team-card">
          <div className="team-header-large">
            <TeamHelmet
              abbreviation={game.homeTeam.abbreviation}
              size="lg"
              className="team-helmet-large"
            />
            <div className="team-info-large">
              <div className="team-name-large">{game.homeTeam.abbreviation}</div>
              {game.player1User && (
                <div className="coach-info">
                  <UserAvatar
                    displayName={game.player1User.displayName}
                    discordId={game.player1User.discordId}
                    discordAvatar={game.player1User.discordAvatar}
                    size="md"
                    className="coach-avatar"
                  />
                  <span>{game.player1User.displayName}</span>
                  <span className="player-badge">Home</span>
                </div>
              )}
            </div>
            <div className="team-score-large">{game.homeTeam.score}</div>
          </div>
          {renderTeamStatsGrid(game.homeTeam)}
        </div>

        <div className="vs-large">vs</div>

        <div className="team-card">
          <div className="team-header-large">
            <TeamHelmet
              abbreviation={game.awayTeam.abbreviation}
              size="lg"
              className="team-helmet-large"
            />
            <div className="team-info-large">
              <div className="team-name-large">{game.awayTeam.abbreviation}</div>
              {game.player2User && (
                <div className="coach-info">
                  <UserAvatar
                    displayName={game.player2User.displayName}
                    discordId={game.player2User.discordId}
                    discordAvatar={game.player2User.discordAvatar}
                    size="md"
                    className="coach-avatar"
                  />
                  <span>{game.player2User.displayName}</span>
                  <span className="player-badge">Away</span>
                </div>
              )}
            </div>
            <div className="team-score-large">{game.awayTeam.score}</div>
          </div>
          {renderTeamStatsGrid(game.awayTeam)}
        </div>
      </div>

      <div className="quarter-summary">
        <h3>Quarter Scores</h3>
        <table>
          <thead>
            <tr>
              <th>Team</th>
              <th>Q1</th>
              <th>Q2</th>
              <th>Q3</th>
              <th>Q4</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TeamHelmet
                    abbreviation={game.homeTeam.abbreviation}
                    size="sm"
                    className="quarter-helmet"
                  />
                  <span>{game.homeTeam.abbreviation}</span>
                </div>
              </td>
              {game.homeTeam.quarterScores.length > 0 ? (
                game.homeTeam.quarterScores.map((score, index) => (
                  <td key={`home-q${index}`}>{score}</td>
                ))
              ) : (
                <>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                </>
              )}
              <td className="total">{game.homeTeam.score}</td>
            </tr>
            <tr>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TeamHelmet
                    abbreviation={game.awayTeam.abbreviation}
                    size="sm"
                    className="quarter-helmet"
                  />
                  <span>{game.awayTeam.abbreviation}</span>
                </div>
              </td>
              {game.awayTeam.quarterScores.length > 0 ? (
                game.awayTeam.quarterScores.map((score, index) => (
                  <td key={`away-q${index}`}>{score}</td>
                ))
              ) : (
                <>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                </>
              )}
              <td className="total">{game.awayTeam.score}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="game-meta-info">
        <div className="meta-item">
          <span className="meta-label">Uploaded:</span>
          <span className="meta-value">{formatDate(game.uploadedAt)}</span>
        </div>
      </div>

      {renderTeamPlayers(game.homeTeam)}
      {renderTeamPlayers(game.awayTeam)}
    </div>
  )
}

